import { createDefaultFormatter } from "./default-formatter";
import type { FormatterKeyPredicate, ITrackingFormatter, ITrackingFormatterRegistry } from "./types";

type FormatterEntry<Key extends string, Value, Result> = {
    formatter: ITrackingFormatter<Key, Value, Result>;
};

export const createTrackingFormatterRegistry = <
    Key extends string = string,
    Value = unknown,
    Result = string,
>(): ITrackingFormatterRegistry<Key, Value, Result> => {
    const defaultFormatter = createDefaultFormatter<Key, Value, Result>();

    const exactKeyMap = new Map<Key, FormatterEntry<Key, Value, Result>[]>();
    const predicateFormatters: FormatterEntry<Key, Value, Result>[] = [{ formatter: defaultFormatter }];
    const allFormatters = new Set<ITrackingFormatter<Key, Value, Result>>([defaultFormatter]);

    const addFormatterToList = (formatter: ITrackingFormatter<Key, Value, Result>): void => {
        const entry: FormatterEntry<Key, Value, Result> = { formatter };

        if (typeof formatter.keys === "function") {
            predicateFormatters.splice(-1, 0, entry);
        } else {
            const keys = Array.isArray(formatter.keys) ? formatter.keys : [formatter.keys];

            keys.forEach((key) => {
                const entries = exactKeyMap.get(key);

                if (!entries) {
                    exactKeyMap.set(key, [entry]);
                } else {
                    entries.push(entry);
                }
            });
        }

        allFormatters.add(formatter);
    };

    const register = (formatter: ITrackingFormatter<Key, Value, Result>): void => {
        if (allFormatters.has(formatter)) {
            return;
        }

        addFormatterToList(formatter);
    };

    const registerMany = (formatters: readonly ITrackingFormatter<Key, Value, Result>[]): void => {
        formatters.forEach(register);
    };

    const getFormatter = (key: Key): ITrackingFormatter<Key, Value, Result> => {
        const exactMatches = exactKeyMap.get(key);

        if (exactMatches && exactMatches.length > 0) {
            return exactMatches[0].formatter;
        }

        for (const entry of predicateFormatters) {
            const predicate = entry.formatter.keys as FormatterKeyPredicate<Key>;

            if (predicate(key)) {
                return entry.formatter;
            }
        }

        // This should never happen since default formatter matches everything
        // explicit fallback for type safety
        return defaultFormatter;
    };

    const format = (key: Key, value: Value): Result => {
        const formatter = getFormatter(key);

        return formatter.format(key, value);
    };

    return {
        register,
        registerMany,
        format,
    };
};
